import { Database } from '@/integrations/supabase/types';

type Sequence = Database['public']['Tables']['sequences']['Row'];

// Valid sequence table columns based on current schema
const VALID_SEQUENCE_COLUMNS = [
  'id', 'name', 'title', 'description', 'duration', 'created_at', 'updated_at',
  'display_id', 'user_id', 'artist_id', 'genre_id', 'file_url', 'image_url', 
  'video_url', 'album_cover_url', 'spotify_track_id', 'spotify_explicit', 
  'spotify_preview_url', 'spotify_duration_ms', 'spotify_popularity', 'album',
  'price', 'download_count', 'featured', 'is_free', 'software_type',
  'claim_requested_at', 'claim_status', 'claim_verified_at', 'original_creator_info',
  'holiday_type', 'file_type'
] as const;

/**
 * Helper function to safely filter sequence data to only include valid database columns
 * This prevents database errors when UI components include extra properties
 */
export const filterSequenceData = (data: Record<string, unknown>): Partial<Sequence> => {
  const filtered: Partial<Sequence> = {};
  const invalidFields: string[] = [];
  
  for (const key in data) {
    if (VALID_SEQUENCE_COLUMNS.includes(key as (typeof VALID_SEQUENCE_COLUMNS)[number])) {
      const value = data[key];
      if (value !== undefined) {
        (filtered as Record<string, unknown>)[key] = value;
      }
    } else {
      // Log any invalid fields being filtered out
      invalidFields.push(key);
    }
  }
  
  // Explicitly remove any computed fields that might have leaked through
  if ('artist' in filtered) {
    delete (filtered as Record<string, unknown>).artist;
    invalidFields.push('artist (explicitly removed)');
  }
  if ('genre' in filtered) {
    delete (filtered as Record<string, unknown>).genre;
    invalidFields.push('genre (explicitly removed)');
  }
  
  // For security, never allow users to change system fields
  const systemFields = ['id', 'created_at', 'download_count', 'featured', 'claim_requested_at', 'claim_status', 'claim_verified_at'];
  systemFields.forEach(field => {
    if (field in filtered) {
      delete (filtered as Record<string, unknown>)[field];
      invalidFields.push(`${field} (system field removed for security)`);
    }
  });
  
  // Log debugging information in development
  if (process.env.NODE_ENV === 'development') {
    if (invalidFields.length > 0) {
      console.log('Filtered out invalid sequence fields:', invalidFields);
    }
    console.log('Valid sequence data being sent to database:', filtered);
  }
  
  // Validate that critical foreign key fields are UUIDs if present
  if (filtered.artist_id && typeof filtered.artist_id !== 'string') {
    console.error('Invalid artist_id type:', typeof filtered.artist_id, filtered.artist_id);
    delete filtered.artist_id;
  }
  if (filtered.genre_id && typeof filtered.genre_id !== 'string') {
    console.error('Invalid genre_id type:', typeof filtered.genre_id, filtered.genre_id);
    delete filtered.genre_id;
  }

  // Validate bigint fields - convert empty strings to null (PostgreSQL can't parse "" as bigint)
  if (filtered.display_id === '') {
    console.warn('Converting empty string display_id to null');
    filtered.display_id = null;
  }
  if (filtered.file_size === '') {
    console.warn('Converting empty string file_size to null');
    filtered.file_size = null;
  }

  return filtered;
};